import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Calendar,
  ClipboardCheck,
  QrCode,
  Ticket,
  Users,
} from "lucide-react";
import { HERO_FEATURES } from "@/config/heroImages";
import Reveal from "@/components/Reveal";

const FEATURE_GROUPS = [
  {
    icon: Calendar,
    title: "Events & Calendar",
    desc: "Browse events from every society, save them to your calendar and get reminded before they start.",
    points: [
      "Search and filter events by university, society and date",
      "Save events to your personal calendar",
      "Apply to join events with one tap",
    ],
  },
  {
    icon: ClipboardCheck,
    title: "Surveys",
    desc: "Complete surveys from societies and universities to earn points and shape campus life.",
    points: [
      "Answer surveys and earn 10 points each",
      "See survey results and statistics",
      "Create your own surveys as a society",
    ],
  },
  {
    icon: QrCode,
    title: "Activities & QR Check-in",
    desc: "Join activities and check in instantly with a real-time QR code — no queues, no paper.",
    points: [
      "Real-time QR check-in at events",
      "Instant attendance confirmation",
      "Earn 20 points per activity joined",
    ],
  },
  {
    icon: Ticket,
    title: "Coupons & Redeem",
    desc: "Turn your points into real value with coupons from partner businesses across Hong Kong.",
    points: [
      "Redeem coupons with your earned points",
      "Discover offers from partner shops",
      "Use coupons with a simple scan",
    ],
  },
  {
    icon: Users,
    title: "Societies",
    desc: "Follow societies, join their memberships and never miss what they are up to.",
    points: [
      "Follow and subscribe to societies",
      "Join society memberships",
      "Get updates on society events",
    ],
  },
];

/**
 * Public "Features" page.
 */
const FeaturesPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div>
      <section
        className="relative bg-cover bg-center py-16 md:py-20"
        style={{ backgroundImage: `url(${HERO_FEATURES})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/40 to-[#ffa217]/20" />
        <div className="relative z-10 mx-auto max-w-3xl animate-fade-down px-4 text-center">
          <h1 className="text-4xl font-extrabold text-white md:text-5xl">
            {t("Features")}
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-gray-200">
            {t("Everything you need to make the most of your university life.")}
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-6xl space-y-16 px-4">
          {FEATURE_GROUPS.map((group, index) => (
            <Reveal key={group.title}>
              <div
                className={`grid items-center gap-10 md:grid-cols-2 ${
                  index % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div>
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#fff3e0] text-[#ffa217]">
                    <group.icon size={26} />
                  </span>
                  <h2 className="mt-5 text-3xl font-extrabold text-[#333]">
                    {t(group.title)}
                  </h2>
                  <p className="mt-3 leading-relaxed text-gray-600">{t(group.desc)}</p>
                  <ul className="mt-5 space-y-2">
                    {group.points.map((point) => (
                      <li key={point} className="flex items-start gap-2 text-gray-700">
                        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#ffa217]" />
                        {t(point)}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-3xl bg-[#fff8f0] p-10">
                  <div className="flex h-40 items-center justify-center rounded-2xl bg-white shadow-lg">
                    <group.icon size={64} className="text-[#ffa217]" />
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#1f2937] py-16">
        <Reveal className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="text-3xl font-extrabold text-white">
            {t("Ready to get started?")}
          </h2>
          <Link
            to="/signup"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#ffa217] px-8 py-3 font-bold text-white transition hover:bg-[#e08f0e]"
          >
            {t("Join as Student")}
            <ArrowRight size={18} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default FeaturesPage;